import React, { useState, useEffect, useMemo } from 'react';
import { IMarker } from 'react-ace';
import { useQuery } from '@tanstack/react-query';
import { Button } from '~/components/Button';
import { EditorInput } from '~/components/Controls/Editor';
import { BreadcrumbsResourcePage } from '~/components/ResourcePage';
import { VerticalTabs, VerticalTabsLayout } from '~/components/VerticalTabs';
import { ResourceLoader, controllableUseResource } from '~/lib/loader';
import { Intent } from '~/lib/intent';
import { useAbility } from '~/lib/ability';
import { useCopyToClipboard } from '~/lib/useCopyToClipboard';
import { AppAdsAbility } from '~/modules/AppAds/ability';
import { fetchSellerDomains, fetchMissingLines } from '~/modules/AppAds/api';
import { AppAdsNavigation } from '~/modules/AppAds/components/AppAdsNavigation';
import { useSelectedPublisher } from '~/modules/AppAds/components/SelectedPublisherContext';
import { IAppAdsMissingLine } from '~/modules/AppAds/models/app-ads';
import { downloadTxt } from '~/lib/file';
import { ID } from '~/lib/model';
import { useFocusOnMount } from '~/lib/useFocusOnMount';
import { SelectSource } from '~/modules/Bidders/components/SelectSource';
import style from './index.module.scss';

export const MissingLinesView = () => {
  const ability = useAbility(AppAdsAbility);
  const isPublisherMode = ability.isSeller() || ability.isSelfServe();
  const sellerId = ability.sellerId && ability.sellerId();

  const { selectedPublisherId, setSelectedPublisherId } = useSelectedPublisher();
  const [activeDomain, setActiveDomain] = useState<string | undefined>(undefined);
  const [copied, copy] = useCopyToClipboard();
  const contentRef = useFocusOnMount<HTMLDivElement>(isPublisherMode);

  const domainsResult = controllableUseResource(() => {
    if (publisherId) {
      return fetchSellerDomains(publisherId);
    }
    return Promise.resolve([]);
  }, []);

  const publisherId = isPublisherMode ? sellerId : selectedPublisherId;

  useEffect(() => {
    if (publisherId) {
      domainsResult.fetch();
    }
  }, [publisherId]);

  const linesQuery = useQuery({
    queryKey: ['app-ads-missing-lines', publisherId, activeDomain],
    queryFn: () => fetchMissingLines(publisherId, activeDomain as string),
    enabled: Boolean(publisherId && activeDomain),
  });

  const linesResult = {
    isPending: linesQuery.isPending,
    resource: linesQuery.data ?? [],
    setResource: () => undefined,
    fetch: () => void linesQuery.refetch(),
  };

  const domainTabs = useMemo(() => {
    const unique = [...new Set(domainsResult.resource || [])].sort();
    return unique.map((domain) => ({
      key: domain,
      id: domain,
      title: domain,
      tooltip: domain,
    }));
  }, [domainsResult.resource]);

  useEffect(() => {
    if (!activeDomain && domainTabs.length > 0) {
      setActiveDomain(domainTabs[0].id);
    }
  }, [domainTabs]);

  const buildAppAdsText = (lines: IAppAdsMissingLine[]) =>
    lines
      .map((l) =>
        [l.adSystemDomain, l.publisherAccountId, l.accountRelationship, l.certificationId]
          .filter(Boolean)
          .join(',')
      )
      .join('\n');

  const loadedLines = linesResult.resource ?? [];
  const canDownload = loadedLines.length > 0;

  const handleDownload = () => {
    downloadTxt(`app-ads-missing-lines-${activeDomain}.txt`, buildAppAdsText(loadedLines));
  };

  return (
    <ResourceLoader result={domainsResult} message="Loading domains">
      {({ resource: domains, error: domainsError }) => (
        <BreadcrumbsResourcePage
          title="Missing lines"
          toolbar={
            <div className={style.toolbar}>
              {!isPublisherMode && (
                <SelectSource
                  placeholder="Select seller"
                  searchable
                  combobox
                  autoFocus
                  className={style.publisherSelect}
                  value={selectedPublisherId}
                  onChange={(value) => {
                    setSelectedPublisherId(value as ID);
                    setActiveDomain(undefined);
                  }}
                />
              )}

              <Button
                responsive
                intent={Intent.DEFAULT}
                disabled={!canDownload}
                onClick={handleDownload}
                leftIcon={<i className="fa fa-download" />}
              >
                Download
              </Button>
            </div>
          }
        >
          <AppAdsNavigation />
          <div className={`layout-content-box ${style.contentFocusable}`} ref={contentRef} tabIndex={-1}>
            {!isPublisherMode && selectedPublisherId == null ? (
              <div className={style.placeholderNote}>Select seller to display domains</div>
            ) : domainsError ? (
              <div className={style.errorNote}>
                Could not load domains.
                <Button intent={Intent.DEFAULT} onClick={() => domainsResult.fetch()}>
                  Retry
                </Button>
              </div>
            ) : !domains?.length ? (
              <div className={style.placeholderNote}>No domains available for this publisher</div>
            ) : (
              <VerticalTabsLayout
                sidebar={
                  <VerticalTabs
                    items={domainTabs}
                    activeId={activeDomain}
                    onChange={setActiveDomain}
                    autoFocusKey={isPublisherMode ? undefined : selectedPublisherId}
                  />
                }
              >
                {!activeDomain ? (
                  <div className={style.placeholderNote}>Select a domain to view missing lines</div>
                ) : linesQuery.isError ? (
                  <div className={style.errorNote}>
                    Could not load missing lines for {activeDomain}.
                    <Button intent={Intent.DEFAULT} onClick={() => linesQuery.refetch()}>
                      Retry
                    </Button>
                  </div>
                ) : (
                  <ResourceLoader
                    result={linesResult}
                    message={`Loading missing lines for ${activeDomain}`}
                  >
                    {({ resource: lines }) => {
                      const text = lines?.length ? buildAppAdsText(lines) : '';
                      const displayText = text || '# No missing lines';
                      const directMarkers: IMarker[] = (lines || []).reduce<IMarker[]>((acc, line, i) => {
                        if ((line.accountRelationship || '').toUpperCase() === 'DIRECT') {
                          acc.push({
                            startRow: i,
                            startCol: 0,
                            endRow: i,
                            endCol: 1,
                            className: style.editorDirectLine,
                            type: 'fullLine',
                          });
                        }
                        return acc;
                      }, []);
                      const lastChecked = lines?.[0]?.loadDt
                        ? new Date(lines[0].loadDt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
                        : null;
                      return (
                        <div className={style.missingLinesContent}>
                          {lastChecked && (
                            <div className={style.lastChecked}>
                              Last Checked: {lastChecked}
                            </div>
                          )}
                          <div className={style.missingLinesEditor}>
                            <EditorInput
                              mode="text"
                              minLines={5}
                              maxLines={30}
                              disabled
                              markers={directMarkers}
                              value={displayText}
                            />
                          </div>
                          {text && (
                            <div className={style.missingLinesActions}>
                              <Button
                                intent={copied ? Intent.SUCCESS : Intent.PRIMARY}
                                leftIcon={<i className={`fa fa-${copied ? 'check' : 'copy'}`} />}
                                onClick={() => copy(text)}
                              >
                                {copied ? 'Copied' : 'Copy'}
                              </Button>
                            </div>
                          )}
                        </div>
                      );
                    }}
                  </ResourceLoader>
                )}
              </VerticalTabsLayout>
            )}
          </div>
        </BreadcrumbsResourcePage>
      )}
    </ResourceLoader>
  );
};
